import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { brand } from '../../config/brand';
import {
  Info,
  Loader2,
  AlertCircle,
  ChevronDown,
  Send,
  ArrowRight
} from 'lucide-react';
import { getPlatformIcon, getPlatformColor } from '../../utils/platformIcons';
import { useProjects } from '../../contexts/ProjectContext';
import { usePostModal } from '../../contexts/PostModalContext';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { cn } from "../../lib/utils";
import { useHistory } from 'react-router-dom';

// shadcn UI
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { ScrollArea } from "../ui/scroll-area";
import { Label } from "../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "sonner";

import { API_BASE_URL } from '../../config';

const PLATFORM_OPTIONS = [
  { id: 'twitter', name: 'X (Twitter)' },
  { id: 'tiktok', name: 'TikTok' },
  { id: 'youtube', name: 'YouTube' },
  { id: 'instagram', name: 'Instagram' },
  { id: 'threads', name: 'Threads' },
  { id: 'bluesky', name: 'Bluesky' },
  { id: 'tumblr', name: 'Tumblr' },
  { id: 'discord', name: 'Discord' },
  { id: 'telegram', name: 'Telegram' },
];

interface SocialConnectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialPlatform?: string;
  onSuccess?: () => void;
}

export default function SocialConnectionModal({ 
  open, 
  onOpenChange, 
  initialPlatform = 'twitter',
  onSuccess
}: SocialConnectionModalProps): JSX.Element {
  const { projects, activeProject, refreshProjects } = useProjects();
  const { openPostModal, closePostModal } = usePostModal();
  const { updateLocalStorage, localStorage: globalState } = useGlobalState();
  const history = useHistory();
  const token = localStorage.getItem('token');

  const [newAccount, setNewAccount] = useState({ 
    platform: initialPlatform, 
    name: '', 
    handle: '', 
    password: '' 
  });
  const [isConnecting, setIsConnecting] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [showBlueskyHelp, setShowBlueskyHelp] = useState(false);
  const [instagramChoice, setInstagramChoice] = useState<'facebook' | 'instagram' | null>(null);
  const [telegramCode, setTelegramLinkCode] = useState('');
  const [isFetchingCode, setIsFetchingCode] = useState(false);
  const [telegramStatus, setTelegramStatus] = useState<{
    isUserLinked: boolean;
    detectedChats: { id: string, name: string, type: string, hasPermissions: boolean }[];
  }>({ isUserLinked: false, detectedChats: [] });
  const [selectedTelegramChatId, setSelectedTelegramChatId] = useState<string | null>(null);

  // Fetch Telegram code if selected and start polling
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (open && newAccount.platform === 'telegram') {
      if (!telegramCode) fetchTelegramCode();
      
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/auth/telegram/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setTelegramStatus(response.data);
          
          // Auto-select first chat with permissions if none selected
          if (response.data.detectedChats.length > 0 && !selectedTelegramChatId) {
            const firstValid = response.data.detectedChats.find((c: any) => c.hasPermissions);
            if (firstValid) setSelectedTelegramChatId(firstValid.id);
          }
        } catch (err) {
          console.error("Error polling telegram status", err);
        }
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [open, newAccount.platform, telegramCode, selectedTelegramChatId]);

  const fetchTelegramCode = async () => {
    try {
      setIsFetchingCode(true);
      const response = await axios.get(`${API_BASE_URL}/auth/telegram/link-code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTelegramLinkCode(response.data.code);
    } catch (err) {
      console.error("Error fetching Telegram code");
    } finally {
      setIsFetchingCode(false);
    }
  };

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setNewAccount(prev => ({ ...prev, platform: initialPlatform }));
      setInstagramChoice(null);
      if (activeProject) {
        setSelectedProjectIds([activeProject.id]);
      } else {
        setSelectedProjectIds([]);
      }
    } else {
      // Clear Telegram state when modal closes to stop polling and clean up
      setTelegramLinkCode('');
      setTelegramStatus({ isUserLinked: false, detectedChats: [] });
      setSelectedTelegramChatId(null);
    }
  }, [open, initialPlatform, activeProject]);

  const handleAddAccount = async (forcePlatform?: string, forceMethod?: 'facebook' | 'instagram') => {
    const platform = (typeof forcePlatform === 'string' ? forcePlatform : undefined) || newAccount.platform;
    const isReturningToPost = window.location.pathname === '/' || window.location.search.includes('returnToPost=true');
    
    try {
      setIsConnecting(true);
      if (['twitter', 'tiktok', 'instagram', 'threads', 'tumblr', 'youtube', 'discord', 'telegram'].includes(platform)) {
        const gameIdsParam = selectedProjectIds.join(',');
        let callbackUrl = `${API_BASE_URL}/auth/${platform}/login?gameIds=${gameIdsParam}${isReturningToPost ? '&returnToPost=true' : ''}`;
        
        if (platform === 'instagram' && forceMethod) {
          callbackUrl += `&method=${forceMethod}`;
        }

        try {
          const response = await axios.get(callbackUrl, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.url) {
            // If we are in the PostModal, we should close it before redirecting
            if (isReturningToPost) {
               closePostModal();
            }
            if (platform === 'telegram') {
              window.open(response.data.url, '_blank');
              onOpenChange(false);
              return;
            }
            window.location.href = response.data.url;
            return;
          }
        } catch (e) {
          toast.error(`Error initiating connection to ${newAccount.platform}`);
          return;
        }
      }

      if (newAccount.platform === 'bluesky') {
        const response = await axios.post(`${API_BASE_URL}/auth/bluesky/connect`, {
          identifier: newAccount.handle,
          password: newAccount.password,
          gameIds: selectedProjectIds
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success('Bluesky connected successfully!');

        // Auto-select the new account
        const accountId = response.data.id;
        if (accountId) {
          const currentSelected = globalState.selectedPlatforms || [];
          if (!currentSelected.includes(accountId)) {
            updateLocalStorage({ selectedPlatforms: [...currentSelected, accountId] });
          }
        }

        refreshProjects();
        if (onSuccess) onSuccess();
        onOpenChange(false);
        return;
      }

      const accountToSave = {
        ...newAccount,
        name: newAccount.name.trim() || newAccount.handle,
        gameIds: selectedProjectIds
      };
      
      const response = await axios.post(`${API_BASE_URL}/social-accounts`, accountToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Auto-select the new account
      const accountId = response.data.id;
      if (accountId) {
        const currentSelected = globalState.selectedPlatforms || [];
        if (!currentSelected.includes(accountId)) {
          updateLocalStorage({ selectedPlatforms: [...currentSelected, accountId] });
        }
      }

      toast.success('Account connected successfully');
      refreshProjects();
      if (onSuccess) onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error adding connection.');
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border text-foreground sm:max-w-[450px] p-0 overflow-hidden">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-2xl font-bold tracking-tight">Connect Account</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Add a new social media profile to your dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-6">
          <div className="space-y-3">
            <Label className="text-muted-foreground uppercase text-[10px] font-black tracking-widest">Select Platform</Label>
            <div className="grid grid-cols-3 gap-2">
              {PLATFORM_OPTIONS.map((option) => {
                const isSelected = newAccount.platform === option.id;
                const color = getPlatformColor(option.id);

                return (
                  <Button
                    key={option.id}
                    type="button"
                    variant={isSelected ? "default" : "outline"}
                    className={cn(
                      "h-14 flex flex-col items-center justify-center gap-2 transition-all",
                      isSelected
                        ? "bg-primary hover:bg-primary/90 border-primary text-white shadow-lg shadow-primary/20"
                        : "bg-background border-border hover:bg-card hover:border-border text-foreground"
                    )}
                    onClick={() => {
                      setNewAccount({ ...newAccount, platform: option.id });
                      setInstagramChoice(null);
                    }}
                  >
                    <div className={cn(
                      "flex items-center justify-center",
                      isSelected ? "text-white" : `text-[${color}]`
                    )}>
                      {getPlatformIcon(option.id, 'h-5 w-5')}
                    </div>
                    <span className={cn(
                      "text-xs font-bold",
                      isSelected ? "text-white" : "text-muted-foreground"
                    )}>
                      {option.name}
                    </span>
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-muted-foreground uppercase text-[10px] font-black tracking-widest">Assign to Projects</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-between h-11 bg-background border-border text-foreground hover:bg-card hover:border-border",
                    selectedProjectIds.length === 0 && "border-red-500/50"
                  )}
                >
                  <span className="text-xs font-bold">
                    {selectedProjectIds.length === 0
                      ? "Select projects..."
                      : selectedProjectIds.length === 1
                      ? projects.find(p => p.id === selectedProjectIds[0])?.name || "1 project selected"
                      : `${selectedProjectIds.length} projects selected`}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0 bg-card border-border" align="start">
                <ScrollArea className="h-[200px] rounded-lg">
                  <div className="p-2 space-y-2">
                    {projects.map((project) => (
                      <div
                        key={project.id}
                        className="flex items-center space-x-3 group cursor-pointer p-2 rounded-lg hover:bg-card transition-colors"
                        onClick={() => {
                          const exists = selectedProjectIds.includes(project.id);
                          setSelectedProjectIds(
                            exists
                              ? selectedProjectIds.filter((id) => id !== project.id)
                              : [...selectedProjectIds, project.id]
                          );
                        }}
                      >
                        <Checkbox
                          id={`project-${project.id}`}
                          checked={selectedProjectIds.includes(project.id)}
                          className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        />
                        <Label
                          htmlFor={`project-${project.id}`}
                          className="text-foreground group-hover:text-foreground transition-colors cursor-pointer flex-1 text-sm font-medium"
                        >
                          {project.name}
                        </Label>
                      </div>
                    ))}
                    {projects.length === 0 && (
                      <p className="text-muted-foreground text-xs italic text-center py-4">
                        No projects found. Create one first.
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
            {selectedProjectIds.length === 0 && projects.length > 0 && (
              <p className="text-[10px] text-red-400 font-bold flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> Select at least one project
              </p>
            )}
          </div>

          {newAccount.platform === 'instagram' && !instagramChoice ? (
            <div className="space-y-4 animate-in fade-in zoom-in-95 duration-300">
              <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5">{getPlatformIcon('instagram', 'h-6 w-6')}</span>
                  <div>
                    <p className="text-sm font-bold text-foreground">Connect Instagram</p>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                      Choose how you want to connect your account.
                    </p>
                  </div>
                </div>

                <div className="grid gap-3">
                  <Button 
                    className="w-full bg-[#1877F2] hover:bg-[#166fe5] text-white font-bold h-14 rounded-xl shadow-lg flex flex-col items-center justify-center py-8"
                    onClick={() => setInstagramChoice('facebook')}
                    disabled={selectedProjectIds.length === 0}
                  >
                    <div className="flex items-center gap-2">
                      {getPlatformIcon('facebook', 'h-5 w-5')}
                      <span>Continue with Facebook</span>
                    </div>
                    <span className="text-[10px] opacity-80 font-normal mt-1">(Recommended for Business/Creators)</span>
                  </Button>

                  <Button 
                    variant="outline"
                    className="w-full border-border bg-transparent hover:bg-card text-foreground font-bold h-14 rounded-xl flex flex-col items-center justify-center py-8"
                    onClick={() => setInstagramChoice('instagram')}
                    disabled={selectedProjectIds.length === 0}
                  >
                    <div className="flex items-center gap-2">
                      {getPlatformIcon('instagram', 'h-5 w-5')}
                      <span>Connect with Instagram</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground font-normal mt-1">(No Facebook Page required)</span>
                  </Button>
                </div>
                
                <p className="text-[10px] text-muted-foreground text-center italic">
                  Note: Direct Instagram login might have limitations for some publishing features.
                </p>
              </div>
            </div>
          ) : ['twitter', 'tiktok', 'instagram', 'threads', 'tumblr', 'youtube', 'discord'].includes(newAccount.platform) ? (
            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 space-y-4 animate-in zoom-in-95 duration-300">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-primary mt-0.5" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {newAccount.platform === 'instagram' && instagramChoice === 'facebook' ? (
                    <>Connecting via <strong>Facebook</strong> allows full access to Instagram Business features like publishing Reels and viewing advanced insights.</>
                  ) : newAccount.platform === 'instagram' && instagramChoice === 'instagram' ? (
                    <>Connecting <strong>Directly</strong> is faster but requires your account to be a Professional account (Business or Creator).</>
                  ) : (
                    <>Connecting to <strong className="text-primary capitalize">{newAccount.platform}</strong> requires official OAuth2 authorization. You will be redirected to their secure login page.</>
                  )}
                </p>
              </div>
              <Button 
                className="w-full font-bold h-12 rounded-xl transition-all shadow-lg active:scale-[0.98] bg-primary hover:bg-primary/90 text-white"
                onClick={() => handleAddAccount(undefined, instagramChoice || undefined)}
                disabled={selectedProjectIds.length === 0 || isConnecting}
              >
                {isConnecting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : (
                  newAccount.platform === 'twitter' ? (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="mr-2 h-5 w-5 fill-white">
                      <path d="M18.244 2.25h3.308l-7.227 3.779 8.502 11.221h-6.657l-5.214-6.817-5.966 6.817H1.676l7.728-8.831L1.242 2.25h6.826l4.704 6.231 5.472-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
                    </svg>
                  ) : 
                  newAccount.platform === 'instagram' ? <span className="mr-2">{getPlatformIcon('instagram', 'h-5 w-5')}</span> :
                  newAccount.platform === 'threads' ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mr-2 h-5 w-5">
                      <path d="M12 12c0-3 2.5-5.5 5.5-5.5S23 9 23 12c0 3.5-3 6.5-6.5 6.5h-4.5c-3.5 0-6.5-3-6.5-6.5S8.5 5.5 12 5.5s6.5 3 6.5 6.5" />
                    </svg>
                  ) :
                  newAccount.platform === 'tumblr' ? (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="mr-2 h-5 w-5">
                      <path d="M14.563 24c-5.093 0-7.031-3.756-7.031-6.411V9.747H5.116V6.648c3.63-1.313 4.512-4.596 4.71-6.469C9.84.051 9.941 0 10.15 0h3.905v6.337h3.308v3.41H14.05v7.093c0 .79.533 1.54 1.51 1.54.629 0 1.226-.189 1.638-.459l.633 3.303c-.566.415-1.811 1.276-3.268 1.276z"/>
                    </svg>
                  ) :
                  newAccount.platform === 'youtube' ? <span className="mr-2">{getPlatformIcon('youtube', 'h-5 w-5')}</span> :
                  newAccount.platform === 'discord' ? (
                    <svg viewBox="0 0 127.14 96.36" fill="currentColor" className="mr-2 h-5 w-5">
                      <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.15,0,0,0,19.39,8.09C2.71,32.65-1.82,56.6.48,80.21a105.73,105.73,0,0,0,32.28,16.15,77.7,77.7,0,0,0,7.37-12,67.45,67.45,0,0,1-11.87-5.67c.9-1,1.81-2,2.69-3.07a74.1,74.1,0,0,0,65.42,0c.88,1.07,1.79,2.1,2.69,3.07a67.4,67.4,0,0,1-11.88,5.67,77.91,77.7,0,0,0,7.36,12,105.42,105.73,0,0,0,32.33-16.15C129.66,50.55,125.83,26.83,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5.12-12.67,11.44-12.67S54,46,53.86,53,48.74,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5.12-12.67,11.44-12.67S96.15,46,96,53,90.89,65.69,84.69,65.69Z"/>
                    </svg>
                  ) :
                  newAccount.platform === 'telegram' ? (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="mr-2 h-5 w-5">
                      <path d="M21.665 3.717l-17.73 7.834c-1.21.541-1.203 1.22-.22 1.528l4.556 1.42 10.54-6.647c.498-.303.953-.14.579.192l-8.54 7.702-.334 4.99c.49 0 .706-.224.98-.495l2.354-2.29 4.896 3.618c.904.499 1.555.242 1.78-.84l3.213-15.146c.33-1.32-.505-1.928-1.36-.52z"/>
                    </svg>
                  ) :
                  <span className="mr-2">{getPlatformIcon(newAccount.platform, 'h-5 w-5')}</span>
                )}
                {newAccount.platform === 'instagram' && instagramChoice === 'facebook' ? 'Authorize via Facebook' : 
                 newAccount.platform === 'instagram' && instagramChoice === 'instagram' ? 'Authorize via Instagram' :
                 `Authorize with ${
                  newAccount.platform === 'twitter' ? 'X (Twitter)' : 
                  newAccount.platform === 'instagram' ? 'Instagram' :
                  newAccount.platform === 'threads' ? 'Threads' :
                  newAccount.platform === 'tumblr' ? 'Tumblr' :
                  newAccount.platform === 'youtube' ? 'YouTube' :
                  newAccount.platform === 'discord' ? 'Discord' :
                  newAccount.platform === 'telegram' ? 'Telegram' :
                  'TikTok'
                }`}
              </Button>
              {newAccount.platform === 'instagram' && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full text-muted-foreground hover:text-foreground"
                  onClick={() => setInstagramChoice(null)}
                >
                  Change method
                </Button>
              )}
            </div>
          ) : newAccount.platform === 'telegram' ? (
            <div className="space-y-4 animate-in zoom-in-95 duration-300">
              <div className="bg-background border border-border rounded-2xl p-4 flex flex-col items-center gap-4">
                <div className="flex flex-col items-center text-center gap-1">
                  <p className="text-xs font-black text-primary uppercase tracking-[0.2em]">Linking Code</p>
                  <p className="text-[9px] text-muted-foreground">Send this code to the bot to link your account</p>
                </div>
                
                <div className="bg-primary/10 text-primary font-mono text-3xl font-black tracking-[0.2em] px-6 py-3 rounded-2xl border-2 border-primary/30 shadow-[0_0_25px_-10px_hsl(var(--primary)/0.3)]">
                  {isFetchingCode ? <Loader2 className="h-6 w-6 animate-spin opacity-20" /> : (telegramCode || '------')}
                </div>

                <div className="w-full space-y-2">
                  {/* Step 1: Link Account */}
                  <div className={cn(
                    "flex items-center justify-between p-2 rounded-xl border transition-all",
                    telegramStatus.isUserLinked ? "bg-green-500/10 border-green-500/30" : "bg-card/50 border-border/50"
                  )}>
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold",
                        telegramStatus.isUserLinked ? "bg-green-500 text-white" : "bg-muted text-muted-foreground"
                      )}>
                        {telegramStatus.isUserLinked ? "✓" : "1"}
                      </div>
                      <div className="flex flex-col">
                        <span className={cn("text-xs font-bold", telegramStatus.isUserLinked ? "text-green-400" : "text-foreground")}>
                          {telegramStatus.isUserLinked ? "Account Linked" : `Waiting to link with @${brand.telegramBot}`}
                        </span>
                        {!telegramStatus.isUserLinked && (
                          <button 
                            className="text-[9px] text-primary font-bold hover:underline text-left"
                            onClick={() => window.open(`https://t.me/${brand.telegramBot}`, '_blank')}
                          >
                            Open bot, or send this code to @{brand.telegramBot} in Telegram
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Add to Group & Detect */}
                  <div className={cn(
                    "flex items-center justify-between p-2 rounded-xl border transition-all",
                    telegramStatus.detectedChats.some(c => c.hasPermissions) ? "bg-green-500/10 border-green-500/30" : "bg-card/50 border-border/50"
                  )}>
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold",
                        telegramStatus.detectedChats.some(c => c.hasPermissions) ? "bg-green-500 text-white" : "bg-muted text-muted-foreground"
                      )}>
                        {telegramStatus.detectedChats.some(c => c.hasPermissions) ? "✓" : "2"}
                      </div>
                      <div className="flex flex-col">
                        <span className={cn("text-xs font-bold", telegramStatus.detectedChats.some(c => c.hasPermissions) ? "text-green-400" : "text-foreground")}>
                          Bot added & Admin permissions
                        </span>
                        {!telegramStatus.detectedChats.some(c => c.hasPermissions) && (
                          <div className="flex flex-col gap-1 mt-1">
                            <span className="text-[9px] text-muted-foreground leading-tight">
                              Add the @{brand.telegramBot} to any channel, and promote as Admin or any group with "Write permissions"
                            </span>
                            <span className="text-[9px] text-muted-foreground italic">Waiting for detection...</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Chat Selector if multiple detected */}
                  {telegramStatus.detectedChats.length > 1 && (
                    <div className="space-y-1 pt-1">
                      <Label className="text-[9px] uppercase font-black text-muted-foreground">Select Chat</Label>
                      <div className="grid gap-1">
                        {telegramStatus.detectedChats.map(chat => (
                          <button
                            key={chat.id}
                            className={cn(
                              "text-xs text-left p-2 rounded-lg border transition-all flex items-center justify-between",
                              selectedTelegramChatId === chat.id ? "bg-primary border-primary text-white" : "bg-card border-border text-muted-foreground hover:border-border"
                            )}
                            onClick={() => setSelectedTelegramChatId(chat.id)}
                          >
                            <span className="font-bold">{chat.name}</span>
                            {!chat.hasPermissions && <span className="text-[9px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">No Permissions</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button 
                    className={cn(
                      "w-full font-black h-10 rounded-xl transition-all shadow-lg text-xs mt-1",
                      telegramStatus.detectedChats.some(c => c.hasPermissions) 
                        ? "bg-primary hover:bg-primary/90 text-white shadow-primary/20" 
                        : "bg-card text-muted-foreground cursor-not-allowed border-border"
                    )}
                    disabled={!telegramStatus.detectedChats.some(c => c.hasPermissions) || isConnecting}
                    onClick={async () => {
                      try {
                        setIsConnecting(true);
                        const chatToFinalize = selectedTelegramChatId || telegramStatus.detectedChats.find(c => c.hasPermissions)?.id;
                        if (!chatToFinalize) return;

                        await axios.post(`${API_BASE_URL}/auth/telegram/finalize`, {
                          chatIds: [chatToFinalize],
                          gameIds: selectedProjectIds
                        }, {
                          headers: { Authorization: `Bearer ${token}` }
                        });

                        toast.success('Telegram connected successfully!');
                        refreshProjects();
                        if (onSuccess) onSuccess();
                        onOpenChange(false);
                      } catch (err: any) {
                        toast.error(err.response?.data?.error || 'Error finishing Telegram connection');
                      } finally {
                        setIsConnecting(false);
                      }
                    }}
                  >
                    {isConnecting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {telegramStatus.detectedChats.some(c => c.hasPermissions) ? "Complete Connection" : "Waiting for Setup..."}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 animate-in fade-in duration-300">
              <div className="grid gap-2">
                <Label className="text-muted-foreground text-[10px] uppercase font-black tracking-widest">
                  {newAccount.platform === 'bluesky' ? 'Handle or Email' : 'Handle / Profile URL'}
                </Label>
                <Input
                  placeholder={newAccount.platform === 'bluesky' ? 'username.bsky.social' : '@username'}
                  className="bg-background border-border h-11"
                  value={newAccount.handle}
                  onChange={(e) => setNewAccount({ ...newAccount, handle: e.target.value })}
                />
              </div>
              
              {newAccount.platform === 'bluesky' && (
                <div className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground text-[10px] uppercase font-black tracking-widest">App Password</Label>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2 text-[10px] text-primary hover:text-primary/80 hover:bg-primary/10 gap-1"
                      onClick={() => setShowBlueskyHelp(!showBlueskyHelp)}
                    >
                      <Info className="h-3 w-3" />
                      How to get this?
                    </Button>
                  </div>
                  <Input
                    type="text"
                    placeholder="abcd-efgh-ijkl-mnop"
                    className="bg-background border-border h-11"
                    value={newAccount.password}
                    onChange={(e) => setNewAccount({ ...newAccount, password: e.target.value })}
                  />
                  
                  {showBlueskyHelp ? (
                    <Alert className="bg-primary/5 border-primary/20 text-primary/80 mt-2">
                      <Info className="h-4 w-4 text-primary" />
                      <AlertTitle className="text-xs font-bold text-primary">Where to find it:</AlertTitle>
                      <AlertDescription className="text-[11px] leading-relaxed">
                        1. Open <strong>Bluesky</strong> app or website.<br />
                        2. Go to <strong>Settings</strong> &gt; <strong>Advanced</strong>.<br />
                        3. Click on <strong>App Passwords</strong>.<br />
                        4. Click <strong>Add App Password</strong> and give it a name (e.g., "{brand.name}").<br />
                        5. Use the generated password here. <strong>Do not use your main account password!</strong>
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <p className="text-[10px] text-muted-foreground italic mt-1">
                      Create one in Settings &gt; Advanced &gt; App Passwords on Bluesky.
                    </p>
                  )}
                </div>
              )}

              {newAccount.platform !== 'bluesky' && (
                <div className="grid gap-2">
                  <Label className="text-muted-foreground text-[10px] uppercase font-black tracking-widest">Internal Label (Optional)</Label>
                  <Input
                    placeholder="e.g. My Official Profile"
                    className="bg-background border-border h-11"
                    value={newAccount.name}
                    onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                  />
                </div>
              )}
              <Button 
                onClick={() => handleAddAccount()} 
                className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-12 rounded-xl mt-2 shadow-lg shadow-primary/10 active:scale-[0.98]"
                disabled={!newAccount.handle.trim() || (newAccount.platform === 'bluesky' && !newAccount.password) || selectedProjectIds.length === 0 || isConnecting}
              >
                {isConnecting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : 'Connect Account'}
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="p-6 bg-muted/50 border-t border-border">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground hover:text-foreground">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
